import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Clock3, LifeBuoy, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline "was your issue resolved?" card rendered as a system message
 * in support-typed conversations. Only shown to the user side (the
 * admin sees a simple grey confirmation bubble instead).
 *
 * Tapping YES closes the conversation with a thanks message. Tapping
 * NO keeps it open and pings the admin. Ten minutes after the prompt
 * fires the backend auto-closes it with the same thanks message.
 */
export const SupportClosePromptCard = ({
  conversationId,
  onResolved,
  disabled = false,
  dataTestId = 'support-close-prompt-card',
}) => {
  const [busy, setBusy] = useState(null);

  const respond = async (decision) => {
    if (busy || disabled) return;
    setBusy(decision);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/support/conversations/${conversationId}/close-response`,
        { decision },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (typeof onResolved === 'function') {
        onResolved({ decision, data: res.data });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your response');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={dataTestId}
      style={{
        alignSelf: 'stretch',
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        borderRadius: 20,
        padding: '14px 16px',
        margin: '4px 8px',
        boxShadow: 'var(--premium-shadow-nested)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 34, height: 34, borderRadius: 999,
            background: 'var(--accent-surface, #F3ECFF)',
            color: 'var(--accent-purple, #703FF6)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <LifeBuoy size={18} strokeWidth={2.1} />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: 15,
            fontWeight: 700,
            color: 'var(--text)',
          }}>
            Was your issue resolved?
          </p>
          <p style={{
            margin: '2px 0 0',
            fontSize: 12.5,
            color: 'var(--text-2)',
            fontFamily: "'Manrope', sans-serif",
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Clock3 size={11} strokeWidth={2.2} /> Auto-closes in 10 min if no reply
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => respond('yes')}
          disabled={busy !== null || disabled}
          data-testid={`${dataTestId}-yes`}
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 12,
            border: 'none',
            background: 'var(--accent-purple, #703FF6)',
            color: '#FFFFFF',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 13.5,
            fontWeight: 700,
            cursor: busy !== null || disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: busy !== null || disabled ? 0.7 : 1,
            transition: 'transform 160ms ease',
          }}
        >
          <CheckCircle2 size={15} strokeWidth={2.2} />
          {busy === 'yes' ? 'Closing…' : 'Yes, all good'}
        </button>
        <button
          type="button"
          onClick={() => respond('no')}
          disabled={busy !== null || disabled}
          data-testid={`${dataTestId}-no`}
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 12,
            border: '1px solid var(--card-border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 13.5,
            fontWeight: 700,
            cursor: busy !== null || disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: busy !== null || disabled ? 0.7 : 1,
          }}
        >
          <X size={15} strokeWidth={2.2} />
          {busy === 'no' ? 'Sending…' : 'Not yet'}
        </button>
      </div>
    </div>
  );
};

/**
 * Rendered in place of the composer when a support conversation has
 * `support_status === "closed"`. Non-interactive; deep-links the user
 * back to `/support/contact` so they can start a fresh ticket.
 */
export const SupportClosedBanner = ({ onOpenNewTicket, dataTestId = 'support-closed-banner' }) => (
  <div
    data-testid={dataTestId}
    style={{
      padding: '14px 18px',
      borderRadius: 18,
      background: 'var(--surface)',
      border: '1px solid var(--card-border)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      alignItems: 'center',
      textAlign: 'center',
    }}
  >
    <p style={{
      margin: 0,
      fontFamily: "'Inria Serif', serif",
      fontSize: 15,
      fontWeight: 700,
      color: 'var(--text)',
    }}>
      This support conversation is closed
    </p>
    <p style={{
      margin: 0,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 12.5,
      color: 'var(--text-2)',
      lineHeight: 1.5,
    }}>
      Need more help? Send us a new ticket and we'll pick it up right away.
    </p>
    <button
      type="button"
      onClick={onOpenNewTicket}
      data-testid={`${dataTestId}-cta`}
      style={{
        marginTop: 6,
        padding: '9px 14px',
        borderRadius: 999,
        border: 'none',
        background: 'var(--accent-purple, #703FF6)',
        color: '#FFFFFF',
        fontFamily: "'Manrope', sans-serif",
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      Open a new ticket
    </button>
  </div>
);

/**
 * Small "Done" chip rendered on messenger list rows for closed
 * support conversations, and "Support" chip for any support-typed
 * conversation regardless of status.
 */
export const SupportRowBadge = ({ status, dataTestId }) => {
  const isDone = status === 'closed';
  return (
    <span
      data-testid={dataTestId}
      style={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '2px 8px',
        borderRadius: 999,
        marginLeft: 6,
        background: isDone ? 'rgba(31, 142, 42, 0.14)' : 'var(--accent-surface, #F3ECFF)',
        color: isDone ? '#1F8E2A' : 'var(--accent-purple-strong, #6335e0)',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {isDone ? <><CheckCircle2 size={10} strokeWidth={2.4} /> Done</> : 'Support'}
    </span>
  );
};

/**
 * Neutral centered system bubble for support-thanks / support-declined
 * messages. Non-interactive — the interactive Yes/No card lives in
 * `SupportClosePromptCard`.
 */
export const SupportSystemBubble = ({ text, tone = 'neutral', dataTestId }) => {
  const isSuccess = tone === 'success';
  return (
    <div
      data-testid={dataTestId}
      style={{
        alignSelf: 'center',
        maxWidth: '85%',
        padding: '10px 14px',
        borderRadius: 14,
        background: isSuccess ? 'rgba(31, 142, 42, 0.08)' : 'var(--accent-surface, #F3ECFF)',
        color: isSuccess ? '#1F8E2A' : 'var(--text-2)',
        border: `1px solid ${isSuccess ? 'rgba(31, 142, 42, 0.18)' : 'var(--card-border)'}`,
        margin: '4px auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: "'Manrope', sans-serif",
        fontSize: 12.5,
        lineHeight: 1.5,
        textAlign: 'center',
      }}
    >
      {isSuccess ? <CheckCircle2 size={13} strokeWidth={2.4} /> : <LifeBuoy size={13} strokeWidth={2.4} />}
      <span>{text}</span>
    </div>
  );
};

/**
 * Extract a support system message from a message doc. Returns null if
 * the message isn't a support system card, otherwise returns a
 * `{ kind, node }` where `node` is a ready-to-render JSX element.
 */
export const renderSupportSystemMessage = (message, { conversation, isMine, onResolved, dataTestId }) => {
  if (!message || message.message_type !== 'system') return null;
  const kind = message.metadata?.system_kind;
  if (!kind) return null;
  if (kind === 'support_close_prompt') {
    const stillPending = (conversation?.support_status || '') === 'awaiting_close_confirm';
    // Admin viewer sees a static preview so they know the card was sent.
    if (isMine) {
      return {
        kind,
        node: (
          <SupportSystemBubble
            key={message.id}
            text="Sent · Was your issue resolved?"
            dataTestId={dataTestId}
          />
        ),
      };
    }
    return {
      kind,
      node: (
        <SupportClosePromptCard
          key={message.id}
          conversationId={conversation.id}
          onResolved={onResolved}
          disabled={!stillPending}
          dataTestId={dataTestId}
        />
      ),
    };
  }
  if (kind === 'support_thanks') {
    return {
      kind,
      node: (
        <SupportSystemBubble
          key={message.id}
          text={message.content || 'Support conversation closed'}
          tone="success"
          dataTestId={dataTestId}
        />
      ),
    };
  }
  if (kind === 'support_declined') {
    return {
      kind,
      node: (
        <SupportSystemBubble
          key={message.id}
          text={message.content || "User isn't done yet"}
          dataTestId={dataTestId}
        />
      ),
    };
  }
  return null;
};
